import { useRef, useState, useEffect } from "react";
import { useSession } from "./session";
import { initSubscriptionCheckout, initKhipuCheckout } from "@/lib/api";

export default function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { session, subscriptionOverdue, subscriptionDueDate, availableGateways, isLoading, refreshProfile } = useSession();
  const hasWebpay = availableGateways.includes("webpay");
  const hasKhipu = availableGateways.includes("khipu");
  const [paying, setPaying] = useState<"" | "webpay" | "khipu">("");
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [tbData, setTbData] = useState<{ token: string; url: string } | null>(null);
  const paymentResult = new URLSearchParams(window.location.search).get("payment");
  const isPaymentReturn = paymentResult === "success" || paymentResult === "failed" || paymentResult === "cancelled";

  useEffect(() => {
    if (tbData && formRef.current) formRef.current.submit();
  }, [tbData]);

  useEffect(() => {
    if (isPaymentReturn) {
      void refreshProfile();
    }
  }, [isPaymentReturn, refreshProfile]);

  // No bloquear mientras carga, ni cuando no está vencido
  if (isLoading || !subscriptionOverdue || isPaymentReturn) return <>{children}</>;

  const fmtDate = (d: string) => new Date(d).toLocaleDateString("es-CL");

  const handlePayWebpay = async () => {
    if (!session) return;
    setPaying("webpay"); setError(null);
    try {
      const returnUrl = `${window.location.origin}/configuracion`;
      setTbData(await initSubscriptionCheckout(session, returnUrl));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar el pago");
      setPaying("");
    }
  };

  const handlePayKhipu = async () => {
    if (!session) return;
    setPaying("khipu"); setError(null);
    try {
      const returnUrl = `${window.location.origin}/configuracion`;
      const result = await initKhipuCheckout(session, returnUrl);
      window.location.href = result.payment_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar pago con Khipu");
      setPaying("");
    }
  };

  return (
    <>
      {/* App bloqueada en fondo */}
      <div className="pointer-events-none select-none opacity-30 blur-sm fixed inset-0 overflow-hidden">
        {children}
      </div>

      {/* Overlay de bloqueo */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header rojo */}
          <div className="bg-red-600 px-6 py-5 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-fill text-xl" />
              </div>
              <div>
                <h2 className="font-bold text-lg leading-tight">Suscripción vencida</h2>
                <p className="text-red-200 text-sm">
                  Tu sistema está temporalmente bloqueado
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            <p className="text-gray-700 text-sm leading-relaxed">
              Tienes un cobro pendiente con vencimiento{" "}
              <strong>{subscriptionDueDate ? fmtDate(subscriptionDueDate) : "—"}</strong>.
              Para continuar usando el sistema debes regularizar el pago.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <div className="space-y-2">
              {hasWebpay && (
                <button
                  onClick={handlePayWebpay}
                  disabled={!!paying}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {paying === "webpay" ? (
                    <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" /> Redirigiendo...</>
                  ) : (
                    <><i className="ri-bank-card-line" /> Pagar con Webpay Plus</>
                  )}
                </button>
              )}
              {hasKhipu && (
                <button
                  onClick={handlePayKhipu}
                  disabled={!!paying}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {paying === "khipu" ? (
                    <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" /> Redirigiendo...</>
                  ) : (
                    <><i className="ri-smartphone-line" /> Pagar con Khipu</>
                  )}
                </button>
              )}
              {!hasWebpay && !hasKhipu && (
                <p className="text-sm text-gray-500 text-center py-2">
                  Contacta a tu proveedor para regularizar el pago.
                </p>
              )}
            </div>

            <p className="text-xs text-gray-400 text-center">
              Si ya realizaste el pago, contacta a tu proveedor para que lo registre.
            </p>
          </div>
        </div>
      </div>

      {tbData && (
        <form ref={formRef} method="POST" action={tbData.url} style={{ display: "none" }}>
          <input type="hidden" name="token_ws" value={tbData.token} />
        </form>
      )}
    </>
  );
}
